// The navigation entries for the signed-in person — fetched from
// /api/sidebar + /api/navigation and shaped by utils/navEntries.js. The top
// bar and Home both read this, so they show the same pages to the same
// people.

import { useEffect, useMemo, useState } from 'react';
import api from '@app/services/api';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { resolveNavUrl, isExternalNavUrl } from '@platform/app-shell/frontend/utils/navUrls';
import { buildNavEntries, DEFAULT_SIDEBAR_CONFIG } from '@platform/app-shell/frontend/utils/navEntries';
import { workspaceSidebar } from '@platform/app-shell/frontend/utils/workspaces';
import useWorkspaces from '@platform/app-shell/frontend/components/TopNav/useWorkspaces';

// A Stride page collapses to its path; a web address stays whole.
export const resolveUrl = (rawUrl) => resolveNavUrl(rawUrl, window.location.origin);

export default function useNavEntries() {
  const { userGroups } = useAuth();
  const { adminMode } = useTab();
  const [navTree, setNavTree] = useState([]);
  const [sidebarConfig, setSidebarConfig] = useState(DEFAULT_SIDEBAR_CONFIG);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.getNavigation().then((t) => { if (alive && Array.isArray(t)) setNavTree(t); }),
      api.getSidebar().then((cfg) => { if (alive && Array.isArray(cfg) && cfg.length) setSidebarConfig(cfg); }),
    ]).then(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  // A workspace brings its own bar; the main bar (and every site without
  // workspaces) draws exactly what it always has. The Navigation menu tree
  // is the site's one tree, wherever its button is.
  const { active } = useWorkspaces();
  const own = active && !active.isMain;
  const barConfig = useMemo(() => (own ? workspaceSidebar(active) : sidebarConfig), [own, active, sidebarConfig]);

  const entries = useMemo(() => buildNavEntries({
    sidebarConfig: barConfig, navTree, userGroups, adminMode, resolveUrl, isExternal: isExternalNavUrl,
  }), [barConfig, navTree, userGroups, adminMode]);

  return { entries, loaded };
}
